import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useQuery, replaceQueryParameters } from "./hooks";

export const GLOBAL_SEARCH_PARAM = "filter";

const getParamValues = (
  searchParams: URLSearchParams,
  param: string,
): string[] => {
  const values: string[] = [];
  searchParams.forEach((value, key) => {
    if (key === param && value) {
      values.push(value);
    }
  });
  return values;
};

export const useUrlStore = (): {
  setSearchTerm: (searchTerm: string) => void;
  clearSearchTerm: () => void;
  setFilter: (filterName: string, values: string[]) => void;
  clearFilter: (filterName: string) => void;
  getFilters: () => Record<string, string[]>;
  getFilterValues: (filterName: string) => string[];
  clearFilters: () => void;
} => {
  const searchParams = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const setSearchTerm = useCallback(
    (searchTerm: string) => {
      replaceQueryParameters(searchParams, navigate, {
        [GLOBAL_SEARCH_PARAM]: searchTerm,
      });
    },
    [navigate, searchParams],
  );

  const clearSearchTerm = useCallback(() => {
    replaceQueryParameters(searchParams, navigate, {
      [GLOBAL_SEARCH_PARAM]: "",
    });
  }, [navigate, searchParams]);

  const setFilter = useCallback(
    (filterName: string, values: string[]) => {
      const url = new URL(window.location.href);
      url.searchParams.delete(filterName);

      values.forEach((value) => {
        if (value) {
          url.searchParams.append(filterName, value);
        }
      });

      navigate(
        {
          pathname: location.pathname,
          search: url.searchParams.toString(),
        },
        { replace: true },
      );
    },
    [navigate, location],
  );

  const clearFilter = useCallback(
    (filterName: string) => {
      const newParams = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (key !== filterName) {
          newParams.append(key, value);
        }
      });

      navigate(
        {
          pathname: location.pathname,
          search: newParams.toString(),
        },
        { replace: true },
      );
    },
    [navigate, searchParams, location],
  );

  const getFilters = useCallback(() => {
    const filters: Record<string, string[]> = {};
    searchParams.forEach((value, key) => {
      if (value && key !== GLOBAL_SEARCH_PARAM) {
        if (filters[key]) {
          filters[key].push(value);
        } else {
          filters[key] = [value];
        }
      }
    });

    return filters;
  }, [searchParams]);

  const getFilterValues = useCallback(
    (filterName: string): string[] => {
      return getParamValues(searchParams, filterName);
    },
    [searchParams],
  );

  const clearFilters = useCallback(() => {
    const globalSearchValue = searchParams.get(GLOBAL_SEARCH_PARAM);
    const newParams = new URLSearchParams();

    // preserve navigation parameters
    const navigationParams = ["file_id"];
    navigationParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        newParams.set(param, value);
      }
    });

    if (globalSearchValue) {
      newParams.set(GLOBAL_SEARCH_PARAM, globalSearchValue);
    }

    navigate(
      {
        pathname: location.pathname,
        search: newParams.toString(),
      },
      { replace: true },
    );
  }, [navigate, searchParams, location]);

  return {
    setSearchTerm,
    clearSearchTerm,
    clearFilter,
    setFilter,
    getFilters,
    getFilterValues,
    clearFilters,
  };
};
